<template>
  <DashboardLayout>
    <template #header>
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <Link :href="route('settings.roles.index')">
            <Button variant="ghost" size="sm">
              <ArrowLeftIcon class="w-4 h-4 mr-2" />
              Back to Roles
            </Button>
          </Link>
          <div>
            <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {{ isEditing ? 'Edit Role' : 'Create Role' }}
            </h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
              {{ isEditing ? 'Update role details and permissions' : 'Create a new custom role with specific permissions' }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <div class="max-w-5xl mx-auto space-y-6">
      <form @submit.prevent="submit" class="space-y-6">
        <!-- Basic Information Card -->
        <Card>
          <template #header>
            <div class="flex items-center space-x-2">
              <InformationCircleIcon class="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
                Basic Information
              </h2>
            </div>
          </template>

          <div class="space-y-5">
            <!-- Role Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Role Name <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.name"
                type="text"
                placeholder="e.g., Project Manager, Sales Lead, Support Agent"
                required
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                :class="{ 'border-red-500': form.errors.name }"
              />
              <p v-if="form.errors.name" class="mt-1 text-sm text-red-600">
                {{ form.errors.name }}
              </p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Choose a clear, descriptive name for this role
              </p>
            </div>

            <!-- Role Slug (Auto-generated, shown if editing) -->
            <div v-if="isEditing && role.slug">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Slug
              </label>
              <input
                :value="role.slug"
                type="text"
                disabled
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 font-mono"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Auto-generated identifier for this role
              </p>
            </div>

            <!-- Description -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                v-model="form.description"
                rows="3"
                placeholder="Describe the responsibilities, access level, and purpose of this role..."
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                :class="{ 'border-red-500': form.errors.description }"
              ></textarea>
              <p v-if="form.errors.description" class="mt-1 text-sm text-red-600">
                {{ form.errors.description }}
              </p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Provide context about what this role can do and who it's for
              </p>
            </div>

            <!-- Priority and Inherits From -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <!-- Priority -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                  Priority
                </label>
                <input
                  v-model.number="form.priority"
                  type="number"
                  min="0"
                  max="1000"
                  placeholder="0"
                  class="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                  :class="{ 'border-red-500': form.errors.priority }"
                />
                <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600">
                  {{ form.errors.priority }}
                </p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Higher priority roles override lower ones in conflicts
                </p>
              </div>

              <!-- Inherits From -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                  Inherits From
                </label>
                <select
                  v-model="form.inherits_from"
                  class="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                  :class="{ 'border-red-500': form.errors.inherits_from }"
                >
                  <option :value="null">None</option>
                  <option
                    v-for="availableRole in availableRoles"
                    :key="availableRole.id"
                    :value="availableRole.id"
                  >
                    {{ availableRole.name }}
                  </option>
                </select>
                <p v-if="form.errors.inherits_from" class="mt-1 text-sm text-red-600">
                  {{ form.errors.inherits_from }}
                </p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Inherit all permissions from another role
                </p>
              </div>
            </div>
          </div>
        </Card>

        <!-- Permission Selection Card -->
        <Card>
          <template #header>
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <KeyIcon class="w-5 h-5 text-gray-500 dark:text-gray-400" />
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
                  Permissions
                </h2>
              </div>
              <Badge variant="primary" size="md">
                {{ selectedPermissionsCount }} selected
              </Badge>
            </div>
          </template>

          <!-- Search and Actions -->
          <div class="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div class="relative flex-1 max-w-md">
              <MagnifyingGlassIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                v-model="permissionSearch"
                type="text"
                placeholder="Search permissions..."
                class="pl-10 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100"
              />
            </div>

            <div class="flex items-center space-x-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                @click="expandAll"
              >
                <ChevronDownIcon class="w-4 h-4 mr-1" />
                Expand All
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                @click="collapseAll"
              >
                <ChevronUpIcon class="w-4 h-4 mr-1" />
                Collapse All
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                @click="selectAll"
              >
                <CheckIcon class="w-4 h-4 mr-1" />
                Select All
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                @click="deselectAll"
              >
                <XMarkIcon class="w-4 h-4 mr-1" />
                Clear All
              </Button>
            </div>
          </div>

          <!-- Permission Modules -->
          <div class="space-y-3">
            <div
              v-for="module in filteredPermissions"
              :key="module.name"
              class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <!-- Module Header -->
              <button
                type="button"
                @click="toggleModule(module.name)"
                class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div class="flex items-center space-x-3">
                  <component
                    :is="getModuleIcon(module.name)"
                    class="h-6 w-6 text-primary-600 dark:text-primary-400"
                  />
                  <span class="font-semibold text-gray-900 dark:text-gray-100">
                    {{ module.label }}
                  </span>
                  <Badge variant="secondary" size="sm">
                    {{ getModuleSelectedCount(module.name) }} / {{ module.permissions.length }}
                  </Badge>
                </div>
                <ChevronDownIcon
                  :class="[
                    'h-5 w-5 text-gray-500 transition-transform',
                    expandedModules.includes(module.name) ? 'transform rotate-180' : ''
                  ]"
                />
              </button>

              <!-- Module Permissions -->
              <div
                v-if="expandedModules.includes(module.name)"
                class="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
              >
                <!-- Module Actions -->
                <div class="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700">
                  <span class="text-sm text-gray-600 dark:text-gray-400">
                    Select permissions for {{ module.label }}
                  </span>
                  <div class="flex items-center space-x-3">
                    <button
                      type="button"
                      @click="selectAllInModule(module.name)"
                      class="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                    >
                      Select All
                    </button>
                    <span class="text-gray-300 dark:text-gray-600">|</span>
                    <button
                      type="button"
                      @click="deselectAllInModule(module.name)"
                      class="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                <!-- Permission List -->
                <div class="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label
                    v-for="permission in module.permissions"
                    :key="permission.id"
                    class="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                  >
                    <input
                      type="checkbox"
                      :value="permission.id"
                      v-model="form.permission_ids"
                      class="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {{ formatPermissionName(permission.name) }}
                      </p>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {{ permission.description || 'No description' }}
                      </p>
                      <div class="flex items-center space-x-2 mt-1">
                        <Badge variant="info" size="xs">
                          {{ permission.category || 'General' }}
                        </Badge>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- No Permissions Found -->
          <div v-if="filteredPermissions.length === 0" class="text-center py-12">
            <MagnifyingGlassIcon class="h-12 w-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No permissions found
            </h3>
            <p class="text-gray-600 dark:text-gray-400">
              Try adjusting your search terms
            </p>
          </div>

          <!-- Validation Error -->
          <div v-if="form.errors.permission_ids" class="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p class="text-sm text-red-600 dark:text-red-400">
              {{ form.errors.permission_ids }}
            </p>
          </div>
        </Card>

        <!-- Permission Summary Card -->
        <Card v-if="selectedPermissionsCount > 0">
          <template #header>
            <div class="flex items-center space-x-2">
              <ChartBarIcon class="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
                Permission Summary
              </h2>
            </div>
          </template>

          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div
              v-for="summary in moduleSummary"
              :key="summary.name"
              class="text-center p-4 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-lg border border-primary-200 dark:border-primary-800"
            >
              <div class="text-3xl font-bold text-primary-600 dark:text-primary-400">
                {{ summary.count }}
              </div>
              <div class="text-sm text-gray-700 dark:text-gray-300 mt-1">
                {{ summary.label }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {{ Math.round((summary.count / summary.total) * 100) }}% of module
              </div>
            </div>
          </div>

          <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div class="flex items-center space-x-2">
              <InformationCircleIcon class="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <p class="text-sm text-blue-900 dark:text-blue-100">
                <strong>Total: {{ selectedPermissionsCount }} permissions</strong> selected across {{ moduleSummary.length }} modules
              </p>
            </div>
          </div>
        </Card>

        <!-- No Permissions Selected Warning -->
        <Card v-else class="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
          <div class="flex items-start space-x-3">
            <ExclamationTriangleIcon class="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 class="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                No permissions selected
              </h3>
              <p class="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Please select at least one permission for this role. Roles without permissions cannot perform any actions.
              </p>
            </div>
          </div>
        </Card>

        <!-- Form Actions -->
        <div class="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
          <Link :href="route('settings.roles.index')">
            <Button variant="ghost" size="md">
              <XMarkIcon class="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </Link>

          <div class="flex items-center space-x-3">
            <Button
              v-if="isEditing"
              type="button"
              variant="secondary"
              size="md"
              @click="resetForm"
            >
              Reset Changes
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              :disabled="form.processing || selectedPermissionsCount === 0"
              :loading="form.processing"
            >
              <CheckIcon class="w-4 h-4 mr-2" />
              {{ isEditing ? 'Update Role' : 'Create Role' }}
            </Button>
          </div>
        </div>
      </form>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Link, useForm } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';
import {
  ArrowLeftIcon,
  InformationCircleIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  XMarkIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  CubeIcon,
  WrenchIcon,
  BellIcon,
  UserGroupIcon,
  Cog6ToothIcon,
} from '@heroicons/vue/24/outline';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
  role: {
    type: Object,
    default: null,
  },
  permissions: {
    type: Array,
    required: true,
  },
  availableRoles: {
    type: Array,
    default: () => [],
  },
});

const { success, error: showError } = useToast();

// State
const permissionSearch = ref('');
const expandedModules = ref([]);

// Computed
const isEditing = computed(() => !!props.role);

// Form
const form = useForm({
  name: props.role?.name || '',
  description: props.role?.description || '',
  priority: props.role?.priority || 0,
  inherits_from: props.role?.inherits_from || null,
  permission_ids: props.role?.current_permission_ids || [],
});

// Filtered permissions based on search
const filteredPermissions = computed(() => {
  if (!permissionSearch.value) {
    return props.permissions;
  }

  const search = permissionSearch.value.toLowerCase();

  return props.permissions
    .map(module => ({
      ...module,
      permissions: module.permissions.filter(permission =>
        permission.name.toLowerCase().includes(search) ||
        permission.description?.toLowerCase().includes(search) ||
        permission.category?.toLowerCase().includes(search)
      ),
    }))
    .filter(module => module.permissions.length > 0);
});

// Selected permissions count
const selectedPermissionsCount = computed(() => {
  return form.permission_ids.length;
});

// Module summary
const moduleSummary = computed(() => {
  return props.permissions
    .map(module => {
      const selectedCount = module.permissions.filter(p =>
        form.permission_ids.includes(p.id)
      ).length;

      return {
        name: module.name,
        label: module.label,
        count: selectedCount,
        total: module.permissions.length,
      };
    })
    .filter(m => m.count > 0);
});

/**
 * Get module icon component
 */
const getModuleIcon = (moduleName) => {
  const icons = {
    'system': Cog6ToothIcon,
    'it_helpdesk': WrenchIcon,
    'maintenance': WrenchIcon,
    'incidents': BellIcon,
    'dashboard': ChartBarIcon,
    'reports': ChartBarIcon,
    'team': UserGroupIcon,
  };

  return icons[moduleName] || CubeIcon;
};

/**
 * Get selected count for a module
 */
const getModuleSelectedCount = (moduleName) => {
  const module = props.permissions.find(m => m.name === moduleName);
  if (!module) return 0;

  return module.permissions.filter(p => form.permission_ids.includes(p.id)).length;
};

/**
 * Format permission name for display
 */
const formatPermissionName = (name) => {
  // Remove module prefix and format
  // e.g., "it_helpdesk.tickets.create" -> "Tickets Create"
  const parts = name.split('.');
  if (parts.length >= 2) {
    return parts.slice(1).map(p =>
      p.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    ).join(' ');
  }
  return name;
};

/**
 * Toggle module expansion
 */
const toggleModule = (moduleName) => {
  const index = expandedModules.value.indexOf(moduleName);
  if (index > -1) {
    expandedModules.value.splice(index, 1);
  } else {
    expandedModules.value.push(moduleName);
  }
};

/**
 * Expand all modules
 */
const expandAll = () => {
  expandedModules.value = filteredPermissions.value.map(m => m.name);
};

/**
 * Collapse all modules
 */
const collapseAll = () => {
  expandedModules.value = [];
};

/**
 * Select all permissions
 */
const selectAll = () => {
  const allPermissionIds = props.permissions.flatMap(module =>
    module.permissions.map(p => p.id)
  );
  form.permission_ids = [...new Set(allPermissionIds)];
};

/**
 * Deselect all permissions
 */
const deselectAll = () => {
  form.permission_ids = [];
};

/**
 * Select all permissions in a module
 */
const selectAllInModule = (moduleName) => {
  const module = props.permissions.find(m => m.name === moduleName);
  if (!module) return;

  const permissionIds = module.permissions.map(p => p.id);
  form.permission_ids = [...new Set([...form.permission_ids, ...permissionIds])];
};

/**
 * Deselect all permissions in a module
 */
const deselectAllInModule = (moduleName) => {
  const module = props.permissions.find(m => m.name === moduleName);
  if (!module) return;

  const permissionIds = module.permissions.map(p => p.id);
  form.permission_ids = form.permission_ids.filter(id => !permissionIds.includes(id));
};

/**
 * Reset form to initial values
 */
const resetForm = () => {
  form.reset();
};

/**
 * Submit form
 */
const submit = () => {
  if (isEditing.value) {
    form.put(route('settings.roles.update', props.role.id), {
      onSuccess: () => {
        success('Role updated successfully');
      },
      onError: (errors) => {
        showError(errors.message || 'Failed to update role. Please check the form and try again.');
      },
    });
  } else {
    form.post(route('settings.roles.store'), {
      onSuccess: () => {
        success('Role created successfully');
      },
      onError: (errors) => {
        showError(errors.message || 'Failed to create role. Please check the form and try again.');
      },
    });
  }
};

// Auto-expand first module on mount
if (props.permissions.length > 0) {
  expandedModules.value = [props.permissions[0].name];
}
</script>
